/**
 * Runtime config bootstrap. The same image works across staging/prod —
 * nginx's docker-entrypoint.sh substitutes __API_URL__ in index.html from
 * the pod env (API_URL) at container start.
 *
 * Detection chain:
 *   1. window.__STREAMKO_CONFIG__.apiUrl — production runtime, set by the
 *      inline script in index.html and substituted by nginx.
 *   2. import.meta.env.VITE_API_URL — `vite dev` fallback (no nginx in
 *      front, so the placeholder is left literal). Pulled from .env.
 *   3. Empty string — SSR / test contexts where neither is available.
 *
 * If the placeholder is still literal (`__API_URL__`), treat it as not set
 * and fall through to the dev fallback. That keeps `vite dev` and Jest
 * tests working without surprise.
 */

declare global {
  interface Window {
    __STREAMKO_CONFIG__?: {
      apiUrl?: string;
      /** Public OTLP/HTTP traces endpoint (e.g. .../api/default/v1/traces). */
      otelEndpoint?: string;
      /** Full Authorization header value (e.g. `Basic base64(email:token)`). */
      otelToken?: string;
      /** `key=value,key=value` resource attributes (e.g. deployment.environment). */
      otelResourceAttributes?: string;
      /** Second OTLP/HTTP traces endpoint for OneUptime dual-ship (full /v1/traces URL). */
      otelOneUptimeEndpoint?: string;
      /** OneUptime ingest token, sent as the `x-oneuptime-token` header (world-readable). */
      otelOneUptimeToken?: string;
    };
  }
}

/** A runtime value that's set, non-empty, and not a left-behind `__PLACEHOLDER__`. */
function resolved(value: string | undefined): string | undefined {
  return value && !value.includes('__') ? value : undefined;
}

export function getApiUrl(): string {
  if (typeof window !== 'undefined') {
    const cfg = window.__STREAMKO_CONFIG__?.apiUrl;
    if (cfg && !cfg.includes('__API_URL__')) return cfg;
  }
  return (import.meta.env.VITE_API_URL as string | undefined) ?? '';
}

export interface OtelConfig {
  /** Full OTLP/HTTP traces URL the exporter POSTs to. */
  endpoint: string;
  /** Full Authorization header value (e.g. `Basic base64(email:token)`). */
  token?: string;
  /** `key=value,key=value` resource attributes merged onto every span. */
  resourceAttributes?: string;
  /** Optional second OTLP/HTTP traces URL to dual-ship to (OneUptime). */
  oneUptimeEndpoint?: string;
  /** OneUptime ingest token, sent as the `x-oneuptime-token` header. */
  oneUptimeToken?: string;
}

/**
 * OpenTelemetry export config. Runtime values (window.__STREAMKO_CONFIG__, set
 * by nginx's entrypoint from the pod env) take precedence; `vite dev` falls
 * back to VITE_OTEL_* env. Returns null when no endpoint is configured, in
 * which case OTel stays a no-op (dev / observability disabled).
 */
export function getOtelConfig(): OtelConfig | null {
  const cfg = typeof window !== 'undefined' ? window.__STREAMKO_CONFIG__ : undefined;
  const endpoint =
    resolved(cfg?.otelEndpoint) ?? (import.meta.env.VITE_OTEL_ENDPOINT as string | undefined);
  if (!endpoint) return null;
  return {
    endpoint,
    token: resolved(cfg?.otelToken) ?? (import.meta.env.VITE_OTEL_TOKEN as string | undefined),
    resourceAttributes:
      resolved(cfg?.otelResourceAttributes) ??
      (import.meta.env.VITE_OTEL_RESOURCE_ATTRIBUTES as string | undefined),
    oneUptimeEndpoint:
      resolved(cfg?.otelOneUptimeEndpoint) ??
      (import.meta.env.VITE_OTEL_ONEUPTIME_ENDPOINT as string | undefined),
    oneUptimeToken:
      resolved(cfg?.otelOneUptimeToken) ??
      (import.meta.env.VITE_OTEL_ONEUPTIME_TOKEN as string | undefined),
  };
}
